import { useCallback, useEffect } from 'react'
import { z } from 'zod'
import { useStateStorage, StorageKeys } from '@reward-platform/utils'
import { getClientRequestHeader, setClientRequestHeader } from '~/services/clients/nextApiClient'
import { UserValue } from './User.types'
import { useAuthContext } from '../auth'

// TODO: these may need pulling from an opco config - leaving as BA defaults for now
const DEFAULT_USER_COUNTRY_CODE = 'GB'
const DEFAULT_USER_CURRENCY_CODE = 'GBP'

const parseString = (value: unknown, defaultValue: string) => {
  const parsedString = z.string().safeParse(value)

  if (!parsedString.success) {
    return defaultValue
  }

  return parsedString.data
}

const useUserHeaders = (user?: UserValue) => {
  const { isAuthenticated } = useAuthContext()
  const { storage: countryCodeState, setStorage: setCountryCodeState } = useStateStorage({
    key: StorageKeys.USER_COUNTRY_CODE,
    parser: (value) => parseString(value, DEFAULT_USER_COUNTRY_CODE),
  })
  const { storage: currencyCodeState, setStorage: setCurrencyCodeState } = useStateStorage({
    key: StorageKeys.USER_CURRENCY_CODE,
    parser: (value) => parseString(value, DEFAULT_USER_CURRENCY_CODE),
  })

  const resetStorage = useCallback(() => {
    setCountryCodeState(DEFAULT_USER_COUNTRY_CODE)
    setCurrencyCodeState(DEFAULT_USER_CURRENCY_CODE)
  }, [setCountryCodeState, setCurrencyCodeState])

  useEffect(() => {
    if (!isAuthenticated) {
      resetStorage()
    }
  }, [isAuthenticated, resetStorage])

  useEffect(() => {
    if (!user) {
      return
    }

    const userCountryCode = user.person?.countryOfResidence?.identifier
    setCountryCodeState(userCountryCode)

    const userCurrencyCode = user.person?.currencyCode
    setCurrencyCodeState(userCurrencyCode)
  }, [setCountryCodeState, setCurrencyCodeState, user])

  useEffect(() => {
    if (!!countryCodeState && countryCodeState !== getClientRequestHeader('countryCode')) {
      setClientRequestHeader('countryCode', countryCodeState)
    }
    if (!!currencyCodeState && currencyCodeState !== getClientRequestHeader('currencyCode')) {
      setClientRequestHeader('currencyCode', currencyCodeState)
    }
  }, [countryCodeState, currencyCodeState])

  return { countryCode: countryCodeState, currencyCode: currencyCodeState }
}

export default useUserHeaders
